// client/src/components/ClientPubViewer.jsx

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { ref, getDownloadURL, getMetadata } from 'firebase/storage';
import { storage } from '../../firebase';

/* ============================
   CONFIG (tuning knobs)
============================ */
const URL_TTL_MS  = 24 * 60 * 60 * 1000; // 24h cache des getDownloadURL
const META_TTL_MS = 24 * 60 * 60 * 1000; // 24h cache des getMetadata

/* ============================
   Utils typage contenu
============================ */
const kindFromContentType = (ct = '') => {
  const c = (ct || '').toLowerCase();
  if (!c) return null;
  if (c.startsWith('image/')) return 'image';
  if (c === 'application/pdf') return 'pdf';
  if (c.startsWith('video/')) return 'video';
  return 'other';
};
const kindFromUrlExt = (url = '') => {
  const lower = (url || '').toLowerCase();
  if (lower.endsWith('.png') || lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.webp')) return 'image';
  if (lower.endsWith('.pdf')) return 'pdf';
  if (lower.endsWith('.mp4') || lower.endsWith('.webm') || lower.endsWith('.mov')) return 'video';
  return 'other';
};
async function detectKindViaHEAD(url) {
  try {
    const resp = await fetch(url, { method: 'HEAD' });
    const ct = resp.headers.get('content-type') || '';
    return kindFromContentType(ct) || kindFromUrlExt(url) || 'other';
  } catch {
    return kindFromUrlExt(url) || 'other';
  }
}

/* ============================
   Caches localStorage (24h)
   + déduplication des appels
============================ */
const nowMs = () => Date.now();
const urlKey  = (p) => `dlurl_${p}`;
const metaKey = (p) => `meta_${p}`;

const inflightUrl  = new Map(); // path -> Promise<string>
const inflightMeta = new Map(); // path -> Promise<any>

async function getDownloadURLCachedFromPath(storagePath) {
  if (!storagePath) return '';
  const k = urlKey(storagePath);
  try {
    const raw = localStorage.getItem(k);
    if (raw) {
      const { url, ts } = JSON.parse(raw);
      if (url && ts && nowMs() - ts < URL_TTL_MS) return url;
    }
  } catch {}

  if (inflightUrl.has(storagePath)) return inflightUrl.get(storagePath);

  const p = (async () => {
    const r = ref(storage, storagePath);
    const url = await getDownloadURL(r);
    try { localStorage.setItem(k, JSON.stringify({ url, ts: nowMs() })); } catch {}
    return url;
  })();

  inflightUrl.set(storagePath, p);
  try {
    const res = await p;
    return res;
  } finally {
    inflightUrl.delete(storagePath);
  }
}

async function getMetadataCachedFromPath(storagePath) {
  if (!storagePath) return null;
  const k = metaKey(storagePath);
  try {
    const raw = localStorage.getItem(k);
    if (raw) {
      const { meta, ts } = JSON.parse(raw);
      if (meta && ts && nowMs() - ts < META_TTL_MS) return meta;
    }
  } catch {}

  if (inflightMeta.has(storagePath)) return inflightMeta.get(storagePath);

  const p = (async () => {
    const r = ref(storage, storagePath);
    const meta = await getMetadata(r);
    try { localStorage.setItem(k, JSON.stringify({ meta, ts: nowMs() })); } catch {}
    return meta;
  })();

  inflightMeta.set(storagePath, p);
  try {
    const res = await p;
    return res;
  } finally {
    inflightMeta.delete(storagePath);
  }
}

/* ============================
   Composant
============================ */
const ClientPubViewer = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Parse des query params (mémo pour éviter recalcul)
  const { mediaPath, logoPath, pseudo, legacyMediaUrl, legacyLogoUrl } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      mediaPath: params.get('path') || '',
      logoPath: params.get('logoPath') || '',
      pseudo: params.get('pseudo') || '',
      legacyMediaUrl: params.get('url') || '',
      legacyLogoUrl: params.get('logo') || '',
    };
  }, [location.search]);

  const [mediaUrl, setMediaUrl] = useState('');
  const [logoUrl, setLogoUrl]   = useState('');
  const [kind, setKind]         = useState('other');
  const [loading, setLoading]   = useState(true);

  const handleBack = useCallback(() => navigate(-1), [navigate]);

  // Styles mémoïsés
  const shellStyle = useMemo(() => ({
    maxWidth: '100%',
    margin: '0 auto',
    padding: '16px 0 0',
  }), []);
  const mediaBox = useMemo(() => ({
    width: '100%',
    height: 'calc(100vh - 200px)',
    maxHeight: '82vh',
    margin: '12px 0 0',
    background: '#fff',
    border: 'none',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'center',
    padding: '0 8px' // marge horizontale pour mobile
  }), []);

  // Chargement hybride (logo + media) avec caches 24h
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);

      try {
        // --- Logo ---
        if (logoPath) {
          const lurl = await getDownloadURLCachedFromPath(logoPath);
          if (!cancelled) setLogoUrl(lurl);
        } else if (legacyLogoUrl) {
          // Aucun I/O: on fait confiance au lien fourni
          if (!cancelled) setLogoUrl(legacyLogoUrl);
        } else {
          if (!cancelled) setLogoUrl('');
        }

        // --- Media principal ---
        if (mediaPath) {
          // 1) Lire metadata (léger) depuis cache 24h
          const meta = await getMetadataCachedFromPath(mediaPath);
          // 2) Déterminer le type via contentType, sinon extension
          const detectedKind = kindFromContentType(meta?.contentType || '') || 'other';
          // 3) Dès que possible, set Kind pour débloquer le rendu du bon viewer
          if (!cancelled) setKind(detectedKind || kindFromUrlExt(mediaPath) || 'other');
          // 4) Récupérer URL (cache 24h)
          const murl = await getDownloadURLCachedFromPath(mediaPath);
          if (!cancelled) {
            setMediaUrl(murl);
            // Si contentType était absent/indéfini, on re-tente une détection via l’URL finale
            if (!detectedKind || detectedKind === 'other') {
              const viaUrl = kindFromUrlExt(murl) || 'other';
              setKind(viaUrl);
            }
          }
        } else if (legacyMediaUrl) {
          // Pas d'accès Storage : on évite le HEAD si extension reconnue
          const quickKind = kindFromUrlExt(legacyMediaUrl);
          if (!cancelled) {
            setMediaUrl(legacyMediaUrl);
            setKind(quickKind);
          }
          if (quickKind === 'other') {
            // Extension inconnue → HEAD (best effort) – garde affichage déjà prêt si HEAD échoue
            const detected = await detectKindViaHEAD(legacyMediaUrl);
            if (!cancelled) setKind(detected || 'other');
          }
        } else {
          if (!cancelled) {
            setMediaUrl('');
            setKind('other');
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('ClientPubViewer load error:', e);
        if (!cancelled) {
          setMediaUrl('');
          setKind('other');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [mediaPath, logoPath, legacyMediaUrl, legacyLogoUrl]);

  return (
    <div className="cpv-page">
      <style>{CPV_STYLES}</style>
      <div className="cpv-shell" style={shellStyle}>
        <div className="cpv-top">
          {/* Bouton retour */}
          <button type="button" onClick={handleBack} className="eb-btn eb-btn--secondary eb-btn--sm cpv-back">
            <FaArrowLeft aria-hidden="true" /> Retour
          </button>

          {/* En-tête logo + pseudo */}
          <div className="cpv-merchant">
            {logoUrl && (
              <img
                src={logoUrl}
                alt="Logo"
                className="cpv-logo"
                loading="lazy"
              />
            )}
            {pseudo && <h2 className="cpv-name">{pseudo}</h2>}
          </div>
        </div>

        {/* Zone media */}
        <div className="cpv-media" style={mediaBox}>
          {loading ? (
            <div className="cpv-state">
              <span className="eb-spinner" aria-hidden="true" />
              <p>Chargement…</p>
            </div>
          ) : !mediaUrl ? (
            <div className="cpv-state">
              <p>Aucun contenu à afficher</p>
            </div>
          ) : kind === 'image' ? (
            <img
              src={mediaUrl}
              alt="Promotion"
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block', margin: 'auto' }}
            />
          ) : kind === 'video' ? (
            <video
              src={mediaUrl}
              controls
              playsInline
              preload="metadata"
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', background: '#000' }}
            />
          ) : kind === 'pdf' ? (
            <iframe
              title="Promotion PDF"
              src={`${mediaUrl}#view=FitH&zoom=80&scrollbar=0&toolbar=0&navpanes=0`}
              style={{
                width: '100%',
                maxWidth: '400px', // largeur réduite pour lisibilité mobile
                height: '100%',
                margin: '0 auto',
                border: 'none',
                backgroundColor: '#fff',
                display: 'block'
              }}
            />
          ) : (
            <div className="cpv-state">
              <p>Aperçu non disponible pour ce type de fichier.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CPV_STYLES = `
.cpv-page {
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 16px 16px 24px;
  background: var(--eb-bg);
}
.cpv-shell { width: 100%; max-width: 900px !important; }
.cpv-top {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.cpv-back { align-self: flex-start; min-height: 40px; gap: 8px; }
.cpv-merchant { display: flex; flex-direction: column; align-items: center; gap: 8px; text-align: center; }
.cpv-logo {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid var(--eb-surface);
  box-shadow: var(--eb-shadow-md);
}
.cpv-name { margin: 0; font-size: 19px; font-weight: 700; color: var(--eb-text); text-align: center; }
.cpv-media {
  margin-top: 16px !important;
  padding: 8px !important;
  height: calc(100dvh - 210px) !important;
  min-height: 320px;
  background: var(--eb-surface) !important;
  border: 1px solid var(--eb-border) !important;
  border-radius: var(--eb-radius-lg);
  box-shadow: var(--eb-shadow-sm);
}
.cpv-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  color: var(--eb-muted);
  font-size: 14.5px;
}
.cpv-state p { margin: 0; }
@media (min-width: 769px) {
  .cpv-shell { margin: 0 auto !important; }
}
`;

export default ClientPubViewer;
